import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Mail, Phone, MapPin, Clock, CheckCircle, MessageSquare, FileX, HelpCircle, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_a85f478b-763e-4b7e-9f54-0dbb5a37a168/artifacts/fk4lgbat_Logo%20Phocea%20Rent.png";
const API = `${window.location.origin}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });

  const subjectOptions = [
    { value: 'general', label: 'Question générale', icon: HelpCircle },
    { value: 'reservation', label: 'Question sur ma réservation', icon: Package },
    { value: 'cancellation', label: 'Demande de résiliation', icon: FileX },
    { value: 'technical', label: 'Support technique', icon: MessageSquare },
    { value: 'other', label: 'Autre', icon: Mail }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSent(true);
      if (typeof window.fbq === 'function') window.fbq('track', 'Contact', {content_name: 'Formulaire contact'});
      toast.success('Message envoyé avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <img 
                src={LOGO_WHITE} 
                alt="Phocea Rent" 
                className="h-10 cursor-pointer"
                onClick={() => navigate('/')}
              />
              <button
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour au site
              </button>
            </div>
          </div>
        </div>

        {/* Success Message */}
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">Message envoyé !</h1>
            <p className="text-lg text-slate-600 mb-8">
              Nous avons bien reçu votre message et nous vous répondrons dans les plus brefs délais.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-8">
              <p className="text-sm text-slate-500">
                Un email de confirmation a été envoyé à <strong>{formData.email}</strong>
              </p>
            </div>
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-8 py-3"
            >
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <img 
              src={LOGO_WHITE} 
              alt="Phocea Rent" 
              className="h-10 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <button
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au site
            </button>
          </div>
        </div>
        
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Contactez-nous</h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Une question ? Une demande ? Notre équipe est là pour vous accompagner.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Contact */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-slate-900 mb-6">Nos coordonnées</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#9aae00]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Téléphone</p>
                    <a href="tel:0486683355" className="text-slate-600 hover:text-[#9aae00]">
                      04 86 68 33 55
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#9aae00]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <a href="mailto:contact@phocearent.fr" className="text-slate-600 hover:text-[#9aae00]">
                      contact@phocearent.fr
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#9aae00]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Adresse</p>
                    <p className="text-slate-600">
                      65 Avenue Jacqueline Auriol<br />
                      13700 Marignane
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#9aae00]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Horaires</p>
                    <p className="text-slate-600">
                      Lun - Ven : 9h - 18h<br />
                      Sam - Dim : Fermé
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Vous avez un compte ?</h3>
              <p className="text-sm text-slate-600 mb-4">
                Connectez-vous à votre espace client pour gérer vos réservations, factures et demandes.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full border-[#9aae00] text-[#9aae00] hover:bg-[#9aae00]/10"
              >
                Accéder à mon espace
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Envoyez-nous un message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Subject Selection */}
                <div>
                  <Label className="text-slate-700 mb-3 block">Sujet de votre demande *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {subjectOptions.map((option) => {
                      const Icon = option.icon;
                      const isSelected = formData.subject === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, subject: option.value }))}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            isSelected 
                              ? 'border-[#DFFF00] bg-[#DFFF00]/10' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-[#9aae00]' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Cancellation Notice */}
                {formData.subject === 'cancellation' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Demande de résiliation :</strong> Veuillez indiquer dans votre message votre numéro de contrat 
                      ou l'email utilisé lors de la réservation afin que nous puissions traiter votre demande rapidement.
                    </p>
                  </div>
                )}

                {/* Name & Email */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Jean Dupont"
                      className="mt-2 h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jean@example.com"
                      className="mt-2 h-12"
                      required
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone">Téléphone (optionnel)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="06 12 34 56 78"
                    className="mt-2 h-12"
                  />
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message">Votre message *</Label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Décrivez votre demande en détail..."
                    className="mt-2 w-full min-h-[150px] px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DFFF00] focus:border-transparent resize-none"
                    required
                  />
                </div>

                {/* Submit */}
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-slate-500">
                    * Champs obligatoires
                  </p>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-8 py-3 h-12"
                  >
                    {sending ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-black py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            © 2024 Phocea Rent - KF Diffusion SARL. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
